import { StyleSheet, Text, View } from "react-native";
import { useTranslation } from "react-i18next";
import { unmetPasswordRules, type PasswordRule } from "@heraion/shared";

import type { UiTheme } from "../theme";

// DS-67 — le regole si vedono MENTRE si scrive, non dopo aver sbagliato.
// Nato dentro WelcomeForm; estratto quando la fase di reset di
// ForgotPasswordScreen è diventata il secondo posto dove una password si
// INVENTA alla cieca. Due copie di questa lista divergerebbero sulla riga che
// nessuno rilegge — l'etichetta parlata «fatto / ancora da fare».
//
// ⛔ Lo stato non è il colore: cambia il SEGNO (✓ / ·) e l'etichetta parlata lo
// dice a voce. A vista ridotta e in monocromia la riga resta leggibile, che è
// il pavimento di casa.
export type PasswordRulesProps = {
  theme: UiTheme;
  /** La password mentre la si scrive. La regola si accende da qui, non da un submit. */
  password: string;
};

export function PasswordRules({ theme, password }: PasswordRulesProps) {
  const { t } = useTranslation();
  const styles = makeStyles(theme);
  const unmet = unmetPasswordRules(password);

  const RULES: { key: PasswordRule; label: string }[] = [
    { key: "length", label: t("auth.passwordRules.length") },
    { key: "uppercase", label: t("auth.passwordRules.uppercase") },
    { key: "lowercase", label: t("auth.passwordRules.lowercase") },
    { key: "number", label: t("auth.passwordRules.number") },
  ];

  // Said aloud only when the SET of missing rules changes: the text is derived
  // from the set, so a keystroke that leaves it alone leaves the string alone
  // and a live region announces nothing. Silent while the field is empty.
  const announcement =
    password.length === 0
      ? null
      : unmet.length === 0
        ? t("auth.passwordRules.met")
        : `${RULES.filter((r) => unmet.includes(r.key))
            .map((r) => r.label)
            .join(", ")}: ${t("auth.passwordRules.unmet")}`;

  return (
    <View style={styles.rules}>
      {announcement !== null ? (
        <Text style={styles.sr} accessibilityLiveRegion="polite">
          {announcement}
        </Text>
      ) : null}
      {RULES.map((rule) => {
        const met = !unmet.includes(rule.key);
        return (
          <Text
            key={rule.key}
            style={[styles.rule, met && styles.ruleMet]}
            accessibilityLabel={`${rule.label}: ${
              met ? t("auth.passwordRules.met") : t("auth.passwordRules.unmet")
            }`}
          >
            {met ? "✓" : "·"}  {rule.label}
          </Text>
        );
      })}
    </View>
  );
}

function makeStyles(theme: UiTheme) {
  const { palette: p, spacing: s, typography: ty } = theme;
  return StyleSheet.create({
    rules: { gap: s.s1 },
    // Spoken, not drawn: the visible list already says it to the eye.
    sr: { position: "absolute", width: 1, height: 1, overflow: "hidden", opacity: 0 },
    // `base` e non `sm`: sono righe che l'atleta DEVE leggere per andare
    // avanti, non decorazione. Il contrasto avanzava (ink3 su bg è 7.04:1,
    // AAA), quindi salire di due punti non costa niente.
    rule: { color: p.ink3, fontSize: ty.sizes.base },
    // ⛔ Il colore ARRIVA DOPO il segno, non al posto suo: `✓` contro `·` regge
    // da solo in monocromia, e la tinta è un rinforzo per chi la vede.
    ruleMet: { color: p.ink2 },
  });
}
